import React from 'react';
import PropTypes from 'prop-types';
import {
  Box,
  Paper,
  Tabs,
  Tab,
  Typography,
  IconButton,
  Collapse,
  Tooltip,
  Divider
} from '@material-ui/core';
import { makeStyles } from '@material-ui/core/styles';
import ExpandMoreIcon from '@material-ui/icons/ExpandMore';
import ExpandLessIcon from '@material-ui/icons/ExpandLess';
import BugReportIcon from '@material-ui/icons/BugReport';
import FileCopyIcon from '@material-ui/icons/FileCopy';

const useStyles = makeStyles((theme) => ({
  root: {
    marginTop: theme.spacing(2),
    borderTop: `1px solid ${theme.palette.divider}`
  },
  header: {
    display: 'flex',
    alignItems: 'center',
    padding: theme.spacing(1),
    backgroundColor: theme.palette.background.default,
    borderBottom: `1px solid ${theme.palette.divider}`
  },
  title: {
    display: 'flex',
    alignItems: 'center',
    gap: theme.spacing(1),
    flex: 1
  },
  content: {
    padding: theme.spacing(2)
  },
  tabPanel: {
    padding: theme.spacing(2),
    backgroundColor: theme.palette.background.paper,
    borderRadius: theme.shape.borderRadius
  },
  codeBlock: {
    margin: theme.spacing(2, 0),
    padding: theme.spacing(2),
    backgroundColor: theme.palette.background.default,
    borderRadius: theme.shape.borderRadius,
    overflow: 'auto',
    maxHeight: '400px',
    '& pre': {
      margin: 0,
      whiteSpace: 'pre-wrap',
      wordBreak: 'break-word'
    }
  },
  promptSection: {
    marginBottom: theme.spacing(2),
    '& > *:not(:last-child)': {
      marginBottom: theme.spacing(1)
    }
  },
  sectionTitle: {
    fontWeight: 500,
    color: theme.palette.text.secondary,
    marginBottom: theme.spacing(1)
  },
  warningText: {
    color: theme.palette.warning.main
  },
  errorText: {
    color: theme.palette.error.main
  },
  label: {
    fontWeight: 500,
    marginBottom: theme.spacing(0.5)
  },
  value: {
    fontFamily: 'monospace',
    backgroundColor: theme.palette.background.default,
    padding: theme.spacing(0.5, 1),
    borderRadius: theme.shape.borderRadius,
    fontSize: '0.875rem'
  }
}));

const TabPanel = ({ children, value, index, ...other }) => (
  <div
    role="tabpanel"
    hidden={value !== index}
    id={`debug-tabpanel-${index}`}
    aria-labelledby={`debug-tab-${index}`}
    {...other}
  >
    {value === index && <Box>{children}</Box>}
  </div>
);

const CodeBlock = ({ title, content, classes, showCopy }) => {
  const [copied, setCopied] = React.useState(false);
  const handleCopy = () => {
    const text = typeof content === 'string' ? content : JSON.stringify(content, null, 2);
    if (navigator && navigator.clipboard) {
      navigator.clipboard.writeText(text).then(() => {
        setCopied(true);
        setTimeout(() => setCopied(false), 1200);
      });
    }
  };
  return (
    <Box className={classes.promptSection}>
      {title && (
        <Box display="flex" alignItems="center" justifyContent="space-between">
          <Typography variant="subtitle2" className={classes.sectionTitle}>
            {title}
          </Typography>
          {showCopy && (
            <Tooltip title={copied ? 'Copied!' : 'Copy to clipboard'}>
              <IconButton size="small" onClick={handleCopy} aria-label={`Copy ${title}`}
                style={{ marginLeft: 8 }}>
                <FileCopyIcon fontSize="small" color={copied ? 'primary' : 'inherit'} />
              </IconButton>
            </Tooltip>
          )}
        </Box>
      )}
      <Box className={classes.codeBlock}>
        <pre>{typeof content === 'string' ? content : JSON.stringify(content, null, 2)}</pre>
      </Box>
    </Box>
  );
};

const LabelValue = ({ label, value, classes }) => (
  <Box mb={1}>
    <Typography variant="body2" className={classes.label}>
      {label}
    </Typography>
    <Typography variant="body2" className={classes.value}>
      {value}
    </Typography>
  </Box>
);

const DebugPanel = ({ debugInfo }) => {
  const classes = useStyles();
  const [isExpanded, setIsExpanded] = React.useState(false);
  const [activeTab, setActiveTab] = React.useState(0);

  const handleTabChange = (event, newValue) => {
    setActiveTab(newValue);
  };

  const toggleExpand = () => {
    setIsExpanded(!isExpanded);
  };

  if (!debugInfo) return null;

  const {
    sessionId,
    eventId,
    questionType,
    prompt,
    response,
    parsing,
    cache,
    refreshControls
  } = debugInfo;

  return (
    <Paper className={classes.root}>
      <Box className={classes.header}>
        <Box className={classes.title}>
          <BugReportIcon />
          <Typography variant="subtitle1">Debug Information</Typography>
        </Box>
        <IconButton onClick={toggleExpand} size="small">
          {isExpanded ? <ExpandLessIcon /> : <ExpandMoreIcon />}
        </IconButton>
      </Box>

      <Collapse in={isExpanded}>
        <Box>
          <Tabs
            value={activeTab}
            onChange={handleTabChange}
            indicatorColor="primary"
            textColor="primary"
            variant="scrollable"
            scrollButtons="auto"
          >
            <Tab label="Overview" />
            <Tab label="Prompt" />
            <Tab label="Response" />
            <Tab label="Parsing" />
            <Tab label="Cache" />
            <Tab label="Actions" />
          </Tabs>

          <Box className={classes.content}>
            <TabPanel value={activeTab} index={0}>
              <Box className={classes.tabPanel}>
                <LabelValue label="Session ID" value={sessionId} classes={classes} />
                <LabelValue label="Event ID" value={eventId} classes={classes} />
                <LabelValue label="Question Type" value={questionType} classes={classes} />
                
                {refreshControls && (
                  <Box mt={2}>
                    <Divider />
                    <Box mt={2}>
                      <Typography variant="subtitle2" className={classes.sectionTitle}>
                        Refresh Controls
                      </Typography>
                      {refreshControls}
                    </Box>
                  </Box>
                )}
              </Box>
            </TabPanel>

            <TabPanel value={activeTab} index={1}>
  <Box className={classes.tabPanel}>
    <CodeBlock
      title="Prompt Configuration"
      content={prompt?.sent}
      classes={classes}
      showCopy={!!prompt?.sent}
    />
    <Divider />
    <Box mt={2}>
      <Typography variant="subtitle2" className={classes.sectionTitle}>
        Prompt Details
      </Typography>
      <LabelValue
        label="Timestamp"
        value={new Date(prompt?.timestamp).toLocaleString()}
        classes={classes}
      />
    </Box>
  </Box>
</TabPanel>

            <TabPanel value={activeTab} index={2}>
  <Box className={classes.tabPanel}>
    <CodeBlock
      title="Raw Response"
      content={response?.raw}
      classes={classes}
      showCopy={!!response?.raw}
    />
    <Divider />
    <Box mt={2}>
      <Typography variant="subtitle2" className={classes.sectionTitle}>
        Response Details
      </Typography>
      <LabelValue
        label="Model"
        value={response?.model}
        classes={classes}
      />
      <LabelValue
        label="Timestamp"
        value={new Date(response?.timestamp).toLocaleString()}
        classes={classes}
      />
    </Box>
  </Box>
</TabPanel>

            <TabPanel value={activeTab} index={3}>
              <Box className={classes.tabPanel}>
                {parsing.validation.warnings.length > 0 && (
                  <Box mb={2}>
                    <Typography variant="subtitle2" className={classes.warningText}>
                      Validation Warnings
                    </Typography>
                    <ul>
                      {parsing.validation.warnings.map((warning, index) => (
                        <li key={index}>
                          <Typography variant="body2" className={classes.warningText}>
                            {warning}
                          </Typography>
                        </li>
                      ))}
                    </ul>
                  </Box>
                )}
                <Divider />
                <Box mt={2}>
                  <Typography variant="subtitle2" className={classes.sectionTitle}>
                    Found Sections
                  </Typography>
                  <ul>
                    {parsing.foundSections.map((section, index) => (
                      <li key={index}>
                        <Typography variant="body2">{section}</Typography>
                      </li>
                    ))}
                  </ul>
                </Box>
                {parsing.missingSections.length > 0 && (
                  <Box mt={2}>
                    <Typography variant="subtitle2" className={classes.sectionTitle}>
                      Missing Sections
                    </Typography>
                    <ul>
                      {parsing.missingSections.map((section, index) => (
                        <li key={index}>
                          <Typography variant="body2" className={classes.errorText}>
                            {section}
                          </Typography>
                        </li>
                      ))}
                    </ul>
                  </Box>
                )}
                <Divider />
                <Box mt={2}>
                  <Typography variant="subtitle2" className={classes.sectionTitle}>
                    Parsing Performance
                  </Typography>
                  <LabelValue
                    label="Duration"
                    value={`${parsing.parsing.duration}ms`}
                    classes={classes}
                  />
                </Box>
              </Box>
            </TabPanel>

            <TabPanel value={activeTab} index={4}>
              <Box className={classes.tabPanel}>
                {/* console.log('[DebugPanel] cache:', cache) */}
<LabelValue
  label="Cache Hit"
  value={cache.fromCache ? 'Yes' : 'No'}
  classes={classes}
/>
                <LabelValue
                  label="Timestamp"
                  value={new Date(cache.timestamp).toLocaleString()}
                  classes={classes}
                />
              </Box>
            </TabPanel>

            <TabPanel value={activeTab} index={5}>
              <Box className={classes.tabPanel}>
                <Typography variant="subtitle2" className={classes.sectionTitle}>
                  Debug Actions
                </Typography>
                {refreshControls || (
                  <Typography variant="body2">
                    No actions available
                  </Typography>
                )}
              </Box>
            </TabPanel>
          </Box>
        </Box>
      </Collapse>
    </Paper>
  );
};

DebugPanel.propTypes = {
  debugInfo: PropTypes.shape({
    sessionId: PropTypes.string,
    eventId: PropTypes.string,
    questionType: PropTypes.string,
    prompt: PropTypes.object,
    response: PropTypes.object,
    parsing: PropTypes.object,
    cache: PropTypes.object,
    refreshControls: PropTypes.node
  })
};

export default DebugPanel; 